import React, { Component, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
import throttle from 'lodash/throttle';
import { token } from '@atlaskit/tokens';
import { injectIntl, type IntlShape } from '@atlassian/jira-intl';
import { gridSize, layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	getDetailViewWidthRatio,
	setDetailViewWidthRatio,
} from '../../../services/software/software-storage.tsx';
import messages from '../../flags/messages.tsx';

type Props = {
	parentWidth: number;
	children: ReactNode;
	useFixedMinWidth?: boolean;
	onResize?: ((arg1: number) => void) | undefined;
	isThemed?: boolean;
	intl: IntlShape;
};

export const MIN_DETAILS_WIDTH_ISSUE_V2 = 495;
export const MIN_DETAILS_WIDTH_ISSUE = 440;
export const MIN_DETAILS_WIDTH_INSIGHTS = 440;
export const WINDOW_RESIZE_THROTTLE = 350;

// remove getMinDetailsWidthIssue and update value in MIN_DETAILS_WIDTH_ISSUE when fix_reply_editor_width is cleaned up
const getMinDetailsWidthIssue = () => {
	return fg('fix_reply_editor_width') ? MIN_DETAILS_WIDTH_ISSUE_V2 : MIN_DETAILS_WIDTH_ISSUE;
};

const IS_DRAGGING_CLASS_NAME = 'ak-dnd-is-dragging';

type State = {
	width: number;
	parentWidth: number;
};

// eslint-disable-next-line jira/react/no-class-components
class Resizer extends Component<Props, State> {
	static defaultProps = {
		onResize: undefined,
		isThemed: false,
	};

	constructor(props: Props) {
		super(props);
		const initValue = getDetailViewWidthRatio();

		this.state = {
			width: Math.max(props.parentWidth * initValue, getMinDetailsWidthIssue()),
			parentWidth: props.parentWidth,
		};
	}

	static getDerivedStateFromProps(nextProps: Props, state: State): State {
		const { parentWidth } = nextProps;
		if (parentWidth !== state.parentWidth) {
			const initValue = getDetailViewWidthRatio();
			return {
				width: Math.max(parentWidth * initValue, getMinDetailsWidthIssue()),
				parentWidth,
			};
		}
		return state;
	}

	componentDidMount() {
		const { useFixedMinWidth, onResize } = this.props;

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('resize', this.onWindowResize);

		onResize?.(useFixedMinWidth ? MIN_DETAILS_WIDTH_INSIGHTS : this.state.width);
	}

	componentDidUpdate(prevProps: Props, prevState: State) {
		const { onResize, useFixedMinWidth } = this.props;

		if (prevProps.useFixedMinWidth !== useFixedMinWidth || prevState.width !== this.state.width) {
			onResize?.(useFixedMinWidth ? MIN_DETAILS_WIDTH_INSIGHTS : this.state.width);
		}
	}

	componentWillUnmount() {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.removeEventListener('resize', this.onWindowResize);
	}

	onWindowResize = throttle(() => {
		const initValue = getDetailViewWidthRatio();
		const newValue = Math.max(this.props.parentWidth * initValue, getMinDetailsWidthIssue());

		if (newValue === this.state.width) {
			return;
		}

		this.setState({
			width: newValue,
		});
	}, WINDOW_RESIZE_THROTTLE);

	onMouseDown = (e: React.MouseEvent) => {
		this.startX = e.pageX + (this.state.width - getMinDetailsWidthIssue());

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('mousemove', this.onMouseMove);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('mouseup', this.onMouseUp);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (document.body) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.body.classList.add(IS_DRAGGING_CLASS_NAME);
		}
	};

	onMouseMove = (e: MouseEvent) => {
		this.extraWidth = Math.max(this.startX - e.pageX, 0);
		const newValue = Math.min(
			getMinDetailsWidthIssue() + this.extraWidth,
			this.props.parentWidth / 2,
		);

		if (newValue === this.state.width) {
			return;
		}

		this.setState({
			width: newValue,
		});
	};

	onMouseUp = () => {
		this.startX = 0;

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.removeEventListener('mousemove', this.onMouseMove);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.removeEventListener('mouseup', this.onMouseUp);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (document.body) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.body.classList.remove(IS_DRAGGING_CLASS_NAME);
		}

		const widthRatio =
			this.state.width === getMinDetailsWidthIssue()
				? 0
				: this.state.width / this.props.parentWidth;
		setDetailViewWidthRatio(widthRatio);
	};

	extraWidth = 0;

	startX = 0;

	render() {
		const { children, useFixedMinWidth, isThemed = false } = this.props;

		return (
			// dynamic change of values
			<Container
				isSoftwareThemingEnabled={isThemed}
				minWidth={useFixedMinWidth ? MIN_DETAILS_WIDTH_INSIGHTS : this.state.width}
			>
				{}
				<ResizeHandle
					onMouseDown={this.onMouseDown}
					data-testid="software-board.detail-view.resizer.resize-handler"
					{...(fg('jfp_a11y_team_scrum_board_appropriate_role') && {
						role: 'slider',
						tabIndex: 0,
						'aria-label': `${this.props.intl.formatMessage(messages.resizeHandleLabel)}`,
						'aria-valuemin': MIN_DETAILS_WIDTH_ISSUE,
						'aria-valuemax': this.state.width,
						'aria-valuenow': this.state.width,
					})}
				/>
				{children}
			</Container>
		);
	}
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled2.div<{ minWidth: string | number; isSoftwareThemingEnabled: boolean }>(
	{
		width: '400px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		minWidth: (props) => `${props.minWidth || 400}px`,
		minHeight: '100%',
		maxHeight: '100%',
		overflowY: 'auto',
		overflowX: 'hidden',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: layers.card,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	(props) =>
		props.isSoftwareThemingEnabled
			? {
					// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
					paddingTop: '1px',
				}
			: {
					backgroundColor: token('elevation.surface'),
				},
);
export default injectIntl(Resizer);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ResizeHandle = styled2.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize}px`,
	cursor: 'ew-resize',
	position: 'relative',
	alignSelf: 'stretch',
	'&::after': {
		content: "''",
		width: '2px',
		height: '20px',
		top: '50%',
		transform: 'translateY(-50%)',
		left: token('space.050'),
		transition: 'background 0.2s',
		position: 'absolute',

		backgroundColor: token('color.border'),
	},
	'&:hover::after, &:active::after': {
		backgroundColor: token('color.background.brand.bold'),
		height: '100%',
	},
});
